import classnames from 'classnames';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import {
  Accordion as AccordionPrimitive,
  type AccordionChildren,
} from '#src/atoms/layout/accordion/index.tsx';

type BaseProps = {
  title: ReactNode;
  children: AccordionChildren;
  noBorder?: boolean;
  noHorizontalPadding?: boolean;
  showChevron?: boolean;
};

// Input-like: setting `expanded` requires `onExpandChange`; else use `startsOpen`.
type Props = BaseProps &
  (
    | {
        expanded: boolean;
        onExpandChange: (expanded: boolean) => void;
        startsOpen?: never;
      }
    | {
        expanded?: never;
        onExpandChange?: (expanded: boolean) => void;
        startsOpen?: boolean;
      }
  );

const Wrapper = styled(AccordionPrimitive.Wrapper)`
  --accordion-padding-inline: var(--double-unit);
  color: var(--c-white);
  border-bottom: 1px solid var(--c-nd-50);

  &.no-border {
    border-bottom: none;
  }
  &.no-horizontal-padding {
    --accordion-padding-inline: 0;
  }
`;

const Title = styled(AccordionPrimitive.Title)`
  gap: var(--double-unit);
  padding: var(--intermediate-unit) var(--accordion-padding-inline);
`;

const Details = styled(AccordionPrimitive.Details)`
  padding: 0 var(--accordion-padding-inline) var(--double-unit);
`;

export const Accordion = ({
  title,
  children,
  expanded,
  startsOpen,
  onExpandChange,
  noBorder = false,
  noHorizontalPadding = false,
  showChevron = true,
}: Props) => (
  <Wrapper
    className={classnames({
      'no-border': noBorder,
      'no-horizontal-padding': noHorizontalPadding,
    })}
    expanded={expanded}
    defaultExpanded={startsOpen}
    onExpandChange={onExpandChange}
  >
    <Title showChevron={showChevron}>{title}</Title>
    <Details>{children}</Details>
  </Wrapper>
);
